import { fetchSiteComprehensiveStatus } from '@/remote/dashboard/comprehensive-status';
import { QUERY_KEYS } from '@/remote/query-key';
import type { SiteComprehensiveStatus } from '@/types/site';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

interface UseBatchSiteComprehensiveStatusOptions {
  enabled?: boolean;
  staleTime?: number;
  gcTime?: number;
  refetchInterval?: number;
}

interface UseBatchSiteComprehensiveStatusReturn {
  data: Record<string, SiteComprehensiveStatus>; // siteId를 키로 하는 객체
  isLoading: boolean;
  errors: Record<string, Error>; // siteId별 에러
  hasErrors: boolean;
  actions: {
    refetchAll: () => void;
    invalidateAll: () => void;
  };
}

export const useBatchSiteComprehensiveStatus = (
  siteIds: string[],
  options: UseBatchSiteComprehensiveStatusOptions = {}
): UseBatchSiteComprehensiveStatusReturn => {
  const queryClient = useQueryClient();

  const {
    enabled = true,
    staleTime = 1000 * 60, // 1분 - 실시간 데이터이므로 짧게 설정
    gcTime = 1000 * 60 * 5, // 5분
    refetchInterval = 1000 * 60, // 1분마다 자동 갱신
  } = options;

  // useQueries를 사용하여 여러 사이트의 데이터를 병렬로 가져오기
  const queries = useQueries({
    queries: siteIds.map((siteId) => ({
      queryKey: QUERY_KEYS.dashboard.comprehensiveStatus(siteId),
      queryFn: () => fetchSiteComprehensiveStatus(siteId),
      enabled: enabled && !!siteId,
      staleTime,
      gcTime,
      refetchInterval,
      retry: 3,
      retryDelay: (attemptIndex: number) =>
        Math.min(1000 * 2 ** attemptIndex, 30000),
    })),
  });

  // 데이터와 상태를 처리
  const data: Record<string, SiteComprehensiveStatus> = {};
  const errors: Record<string, Error> = {};

  queries.forEach((query, index) => {
    const siteId = siteIds[index];

    if (query.data) {
      data[siteId] = query.data;
    }

    if (query.error) {
      errors[siteId] = query.error as Error;
    }
  });

  const isLoading = queries.some((query) => query.isLoading);
  const hasErrors = Object.keys(errors).length > 0;

  const refetchAll = useCallback(() => {
    siteIds.forEach((siteId) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.dashboard.comprehensiveStatus(siteId),
      });
    });
  }, [queryClient, siteIds]);

  const invalidateAll = useCallback(() => {
    siteIds.forEach((siteId) => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.dashboard.comprehensiveStatus(siteId),
      });
    });
  }, [queryClient, siteIds]);

  return {
    data,
    isLoading,
    errors,
    hasErrors,
    actions: {
      refetchAll,
      invalidateAll,
    },
  };
};
